import CustomButton from "@components/common/Button";
import LoadingSpinner from "@components/common/LoadingSpinner";
import NotFound from "@components/common/NotFound";
import { notify } from "@components/common/Notification";
import SMSConfigurationAddModal from "@components/SMSConfigurationAddModal";
import { checkFormChanges } from "@lib/checkFormChanges";
import SMSConfigurationService from "@/services/APIs/SMSConfigurationService";
import { navigationRoutes } from "@/utils/routes";
import { SMSConfiguration } from "@/utils/types/smsConfiguration";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  Form,
  Input,
  Table,
  Tooltip,
  Space,
  FormInstance,
  Popconfirm,
  Checkbox,
} from "antd";
import { ColumnType } from "antd/es/table";
import { Info, Edit2, Save, Plus, Trash } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Helper function to handle form submission

const AddSMSConfigurationPage: React.FC = () => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingData, setEditingData] = useState<SMSConfiguration | null>(null);
  const [gridData, setGridData] = useState<SMSConfiguration[]>([]);
  const [staticParams, setStaticParams] = useState<
    { key: string; value: string; is_header?: boolean }[]
  >([]);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // Fetching specific SMS configuration for edit mode
  const {
    data: smsConfig,
    isLoading: smsConfigLoading,
    isError,
  } = useQuery({
    queryKey: ["smsConfig", id],
    queryFn: () => SMSConfigurationService.getSMSConfigurationById(id ?? ""),
    enabled: !!id,
  });

  console.log("smsConfig", smsConfig);

  useEffect(() => {
    if (isEditMode && smsConfig) {
      form.setFieldsValue({
        name: smsConfig.name,
        gateway_url: smsConfig.gateway_url,
        message_param: smsConfig.message_param,
        receiver_param: smsConfig.receiver_param,
        template_text: smsConfig.template_text,
        use_post: smsConfig.use_post,
        is_header: smsConfig.is_header,
      });
      const formattedStaticParams = smsConfig.static_params.map(param => ({
        id: Date.now().toString(),
        parameter: param.key ?? "",
        value: param.value ?? "",
        message_param: "",
        receiver_param: "",
        use_post: false,
        template_text: "",
        static_params: [], // Empty array, as it's nested
        is_header: param.is_header,
      }));
      setGridData(formattedStaticParams);
    }
  }, [isEditMode, smsConfig, form]);

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingIndex(null);
    setEditingData(null);
  };

  const handleModalSubmit = (data: SMSConfiguration) => {
    setIsModalVisible(false);

    if (!data?.parameter || !data?.value) {
      return;
    }

    setGridData(prev => {
      if (editingIndex !== null && prev[editingIndex]) {
        const prevRow = prev[editingIndex];
        const next = [...prev];
        next[editingIndex] = {
          ...prevRow,
          ...data,
          id: prevRow.id,
        };
        return next;
      }

      // ADD new row
      return [
        ...prev,
        {
          ...data,
          id:
            (globalThis as any).crypto?.randomUUID?.() ?? Date.now().toString(),
        },
      ];
    });

    // cleanup edit state
    setEditingIndex(null);
    setEditingData(null);
  };

  const handleFormSubmit = async (
    form: FormInstance,
    isEditMode: boolean,
    id: string | undefined,
    smsConfig: SMSConfiguration | undefined,
    notify: any,
    setLoading: (loading: boolean) => void
  ) => {
    const values = await form.validateFields();
    setLoading(true);

    const staticParamsValue = [...staticParams];
    const payload = {
      name: values.name,
      gateway_url: values.gateway_url,
      message_param: values.message_param,
      receiver_param: values.receiver_param,
      use_post: values.use_post ?? false,
      template_text: values.template_text,
      static_params: staticParamsValue,
    };

    try {
      if (isEditMode) {
        if (!checkFormChanges(values, smsConfig ?? {}, notify)) {
          return;
        }
        await SMSConfigurationService.updateSMSConfiguration(id!, payload);
        navigate(navigationRoutes.SMS_CONFIGURATION);
      } else {
        await SMSConfigurationService.createSMSConfiguration(payload);
        navigate(navigationRoutes.SMS_CONFIGURATION);
      }
    } catch (error) {
      console.error("Form validation error:", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  // const handleSelectAllChange = (e: CheckboxChangeEvent) => {
  //   if (e.target.checked) {
  //     setSelectedRowKeys(gridData.map((row) => row.id)); // Select all rows
  //   } else {
  //     setSelectedRowKeys([]); // Deselect all rows
  //   }
  // };

  // const handleSelectAllChange = (e: CheckboxChangeEvent) => {
  //   if (e.target.checked) {
  //     setSelectedRowKeys(gridData.map((row) => row.sms_configuration_id)); // Select all rows
  //   } else {
  //     setSelectedRowKeys([]); // Deselect all rows
  //   }
  // };

  const handleEdit = (id: string) => {
    setIsModalVisible(true);
    setEditingIndex(gridData.findIndex(row => row.id === id));
    setEditingData(gridData.find(row => row.id === id) ?? null);
  };

  const handleDelete = (id: string) => {
    setGridData(gridData.filter(row => row.id !== id));
  };
  // Table columns
  const columns: ColumnType<SMSConfiguration>[] = [
    {
      title: "Parameter",
      dataIndex: "parameter",
      key: "parameter",
      render: (_, record: SMSConfiguration) =>
        record?.parameter || "Message Parameter",
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      render: (_, record: SMSConfiguration) => record?.value || "Value",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => handleEdit(record.id)}
          type="text"
          title="Edit Record"
        />,
        <Popconfirm
          key="delete"
          title="Are you sure you want to delete this item?"
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <CustomButton
            danger
            key="delete"
            icon={<Trash size={16} />}
            type="text"
            title="Delete Record"
          />
        </Popconfirm>,
      ],
    },
  ];

  const handleSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);

    // Capture the selected rows' parameter and value
    const selectedRows = gridData.filter(row =>
      newSelectedRowKeys.includes(row.id)
    );

    const updatedStaticParams = selectedRows.map(row => ({
      key: row.parameter,
      value: row.value,
      is_header: row.is_header,
    }));

    setStaticParams(updatedStaticParams); // Update staticParams with selected rows' parameters
  };

  // Handle Select All change

  // Handle Select Row change
  // const handleSelectRow = (id: React.Key) => {
  //   const newSelectedRowKeys = selectedRowKeys.includes(id)
  //     ? selectedRowKeys.filter(key => key !== id) // Deselect the row
  //     : [...selectedRowKeys, id]; // Select the row
  //   setSelectedRowKeys(newSelectedRowKeys);
  // };

  if (isEditMode && smsConfigLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const onAddSMSConfiguration = () => {
    setIsModalVisible(true);
    setEditingIndex(null);
    setEditingData(null);
  };

  if (isEditMode && isError) {
    return <NotFound />;
  }

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={
            isEditMode ? "Edit SMS Configuration" : "Add SMS Configuration"
          }
          onBack={() => window.history.back()}
          extra={
            <CustomButton
              type="primary"
              icon={<Save size={16} />}
              onClick={() =>
                handleFormSubmit(
                  form,
                  isEditMode,
                  id,
                  smsConfig,
                  notify,
                  setLoading
                )
              }
              loading={loading}
              disabled={loading}
              title={
                isEditMode
                  ? "Save SMS Configuration Changes"
                  : "Save New SMS Configuration"
              }
            >
              {loading ? "Saving..." : isEditMode ? "Save Changes" : "Save"}
            </CustomButton>
          }
        />
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Please enter name" }]}
        >
          <Input placeholder="Name" />
        </Form.Item>
        <Form.Item
          name="gateway_url"
          // initialValue={smsConfig?.gateway_url}
          label="SMS Gateway URL"
        >
          <Input.TextArea
            style={{ resize: "none" }}
            rows={3}
            placeholder="https://web.shreesms.net/API/SendSMS.aspx"
          />
        </Form.Item>
        <div className="mt-2 text-sm text-gray-500">
          <p>
            <span>Eg. smsgateway.com/api/send-sms.cgi</span>
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Form.Item
              name="message_param"
              // initialValue={smsConfig?.message_param}
              label="Message Parameter"
            >
              <Input placeholder="MsgText" />
            </Form.Item>
            <div className="mt-2 text-sm text-gray-500">
              <p>
                <span>Enter url parameter for message</span>
              </p>
            </div>
          </div>
          <div>
            <Form.Item
              name="receiver_param"
              initialValue={smsConfig?.receiver_param}
              label="Receiver Parameter"
            >
              <Input placeholder="Mobile" />
            </Form.Item>
            <div className="mt-2 text-sm text-gray-500">
              <p>
                <span>Enter url parameter for receiver mobile number</span>
              </p>
            </div>
          </div>
        </div>
        <Form.Item
          name="template_text"
          initialValue={smsConfig?.template_text}
          label="SMS Template"
        >
          <Input.TextArea
            style={{ resize: "none" }}
            rows={3}
            placeholder="Your Verification code is: {{otp}} ..."
          />
        </Form.Item>

        <Form.Item name="use_post" label="" valuePropName="checked">
          <Checkbox>
            {" "}
            <span>User post</span>
          </Checkbox>
        </Form.Item>

        {/* State and Transition Table Section */}
        <Card
          title={
            <Space>
              <span>SMS Configuration</span>
              <Tooltip title="Define the static parameters for your sms configuration">
                <Info size={16} className="text-gray-400" />
              </Tooltip>
            </Space>
          }
          className="mb-6"
          extra={
            <CustomButton
              type="dashed"
              icon={<Plus size={16} />}
              title="Add New SMS Configuration"
              onClick={onAddSMSConfiguration}
            >
              Add Row
            </CustomButton>
          }
        >
          <Table
            dataSource={gridData}
            columns={columns}
            pagination={false}
            rowSelection={{
              selectedRowKeys,
              columnWidth: 60,
              onChange: handleSelectChange,
              selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT],
            }}
            size="small"
            rowKey={record => record?.id?.toString() ?? "0"}
            scroll={{ x: 1200 }}
            className="workflow-states-table"
          />
        </Card>
        <SMSConfigurationAddModal
          open={isModalVisible}
          onCancel={handleModalCancel}
          onSubmit={handleModalSubmit}
          initialData={editingData}
          isEditMode={editingIndex !== null}
          // disabled={isAnyActionInProgress}
          // confirmLoading={isAnyActionInProgress}
        />
      </Form>
    </Card>
  );
};

export default AddSMSConfigurationPage;
